import { useState } from 'react'
import { config } from '../../config'
import { layout, cx, text, ui } from '../../lib/ui-primitives'
import { Button, Card, Input } from '../primitives'
import { AdminDashboard } from './AdminDashboard'

// ============================================
// ADMIN MODE
// Authentication gate for admin dashboard
// ============================================

interface AdminModeProps {
  isAuthenticated: boolean
  onAuthenticate: (auth: boolean) => void
  onExit: () => void
}

/**
 * AdminMode - Admin authentication and dashboard access
 *
 * Refactored to use:
 * - Unified design tokens from ui-primitives
 * - Primitive components (Button, Card, Input)
 *
 * Features:
 * - Password authentication
 * - Error state handling
 * - Consistent styling with kiosk
 */
export function AdminMode({ isAuthenticated, onAuthenticate, onExit }: AdminModeProps) {
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')

  const handleLogin = () => {
    if (password === config.adminPassword) {
      onAuthenticate(true)
      setError('')
    } else {
      setError('Invalid password')
    }
  }

  // Show dashboard if authenticated
  if (isAuthenticated) {
    return <AdminDashboard onExit={onExit} />
  }

  // Login screen
  return (
    <div className={cx(layout.page, 'flex items-center justify-center p-4')}>
      <Card padding="lg" className="w-full max-w-sm">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="text-4xl mb-3" aria-hidden="true">🔐</div>
          <h2 className={text.h2}>Admin Access</h2>
          <p className={cx('mt-1', text.secondary)}>Enter password to continue</p>
        </div>

        {/* Password Input */}
        <Input
          type="password"
          value={password}
          onChange={(e) => {
            setPassword(e.target.value)
            setError('')
          }}
          onKeyDown={(e) => e.key === 'Enter' && handleLogin()}
          placeholder="Password"
          error={error || undefined}
          className="mb-4"
          aria-label="Admin password"
        />

        {/* Error Message */}
        {error && (
          <div
            className={cx(ui.card, 'bg-red-500/10 border-red-500/30 p-3 text-sm text-center mb-4')}
            role="alert"
          >
            <span className="text-red-400">{error}</span>
          </div>
        )}

        {/* Actions */}
        <div className="space-y-3">
          <Button
            variant="primary"
            fullWidth
            size="lg"
            onClick={handleLogin}
          >
            Login
          </Button>

          <Button
            variant="ghost"
            fullWidth
            onClick={onExit}
          >
            Back to Kiosk
          </Button>
        </div>
      </Card>
    </div>
  )
}
